import { type ReactNode, type RefObject, useEffect, useRef } from "react";
import type { LedgerFinding } from "../../api/codeReview";
import { severityMeta, type FindingGroup } from "../../lib/bobConsole";
import { absoluteTime } from "../../lib/time";
import { fmtAgo, parseTs } from "../../lib/codeReviewFormat";
import { effortOf, headlineOf } from "../../lib/ledgerFormat";
import SeverityChip from "./SeverityChip";

export type GroupBy = "severity" | "push";

const GROUPINGS: ReadonlyArray<{ id: GroupBy; label: string }> = [
  { id: "severity", label: "Severity" },
  { id: "push", label: "Push" },
];

export const STATE_LABELS: Readonly<Record<string, string>> = { open: "Open", fixed: "Fixed", dismissed: "Not a bug" };

export function locationOf(f: LedgerFinding): string {
  return f.line == null ? String(f.file ?? "") : `${f.file}:${f.line}`;
}

/** Who claimed it on Bob's Discord card. A member is never sent the name, only the time. */
export function claimOf(f: LedgerFinding): string | null {
  const by = typeof f.solved_by === "string"
    ? f.solved_by
    : !Object.hasOwn(f, "solved_by") && typeof f.solved_at === "string" ? "Somebody" : "";
  if (!by) return null;
  const ago = f.solved_at ? fmtAgo(f.solved_at) : "";
  return `${by} says this is handled, not verified${ago ? `, ${ago}` : ""}`;
}

function prHref(repo: string | undefined, pr: number): string | null {
  return repo ? `${encodeURI(`https://github.com/${repo}`)}/pull/${pr}` : null;
}

function commitHref(repo: string | undefined, sha: string): string | null {
  return repo ? `${encodeURI(`https://github.com/${repo}`)}/commit/${encodeURI(sha)}` : null;
}

function GroupHead({ group, repo, linked }: { group: FindingGroup; repo: string | undefined; linked: boolean }) {
  let name: ReactNode;
  let note: string | null = null;
  if (group.kind === "severity") {
    name = <SeverityChip severity={group.severity} />;
    note = severityMeta(group.severity)?.groupNote ?? null;
  } else if (group.kind === "pr") {
    const href = prHref(repo, group.pr);
    name = href ? (
      <a className="bob-group-name" href={href} target="_blank" rel="noopener noreferrer">Pull request #{group.pr}</a>
    ) : (
      <span className="bob-group-name" title="no repo on this answer, so the pull request link could not be built">
        Pull request #{group.pr}
      </span>
    );
  } else {
    const sha = group.sha ? group.sha.slice(0, 7) : null;
    const href = group.sha ? commitHref(repo, group.sha) : null;
    name = (
      <span className="bob-chip bob-chip-cat bob-chip-push bob-group-name">
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
          <circle cx="12" cy="12" r="3.5" />
          <path d="M2 12h6.5M15.5 12H22" />
        </svg>
        {sha === null ? "Push, commit not recorded" : href ? (
          <a href={href} target="_blank" rel="noopener noreferrer">Push {sha}</a>
        ) : `Push ${sha}`}
      </span>
    );
    const at = group.at ? absoluteTime(parseTs(group.at)) : "";
    note = at === "" ? null : `first seen ${at}`;
  }
  return (
    <h2 className="bob-group-head" data-group-kind={group.kind}>
      {name}
      {note && <span className="bob-group-note">{note}</span>}
      {linked && (
        <span className="bob-group-linked" title="You followed a link to this pull request, so its findings are first.">
          linked from Bob&apos;s card
        </span>
      )}
      <span className="bob-group-rule" aria-hidden="true" />
      <span className="bob-count">{group.findings.length}</span>
    </h2>
  );
}

const REVEAL_GAP = 8;

/**
 * Brings a card to the top of the list. On a wide screen the list is its own
 * scroll box and only it moves; otherwise the page is the scroller.
 * Never `scrollIntoView`, which also scrolls every ancestor, the page included.
 */
export function revealCard(card: HTMLElement, list: HTMLElement | null): void {
  const top = card.getBoundingClientRect().top;
  const overflow = list ? getComputedStyle(list).overflowY : "visible";
  if (list && (overflow === "auto" || overflow === "scroll")) {
    list.scrollTop += top - list.getBoundingClientRect().top - REVEAL_GAP;
    return;
  }
  window.scrollTo({ top: Math.max(0, window.scrollY + top - REVEAL_GAP) });
}

function FindingCard(
  { finding, selected, scrollTo, onSelect, list }: {
    finding: LedgerFinding;
    selected: boolean;
    scrollTo: boolean;
    onSelect: (id: number) => void;
    list: RefObject<HTMLDivElement>;
  },
) {
  const ref = useRef<HTMLLIElement>(null);
  useEffect(() => {
    if (scrollTo && ref.current) revealCard(ref.current, list.current);
  }, [scrollTo, list]);
  const status = typeof finding.status === "string" ? finding.status : "";
  const effort = effortOf(finding);
  const claim = claimOf(finding);
  const found = finding.created_at ? fmtAgo(finding.created_at) : "";
  return (
    <li ref={ref} className="bob-card-item" data-finding={finding.id} data-selected={selected}>
      <button
        type="button"
        className="bob-card"
        aria-pressed={selected}
        data-status={status}
        onClick={() => onSelect(finding.id)}
      >
        <span className="bob-card-top">
          <SeverityChip severity={finding.severity} />
          {typeof finding.category === "string" && finding.category !== "" && (
            <span className="bob-chip bob-chip-cat">{finding.category}</span>
          )}
          {effort && <span className="bob-chip bob-chip-effort">{effort} fix</span>}
          {(status === "fixed" || status === "dismissed") && (
            <span className="bob-chip bob-chip-state" data-state={status}>{STATE_LABELS[status]}</span>
          )}
          <span className="bob-flex" />
          <span className="bob-mono bob-card-id">#{finding.id}</span>
        </span>
        <span className="bob-card-title">{headlineOf(finding)}</span>
        <span className="bob-card-meta">
          <span className="bob-mono bob-card-loc">{locationOf(finding)}</span>
          {found !== "" && <span>found {found}</span>}
          {claim && <span className="bob-claim">{claim}</span>}
        </span>
      </button>
    </li>
  );
}

/** The middle pane: search, grouping and the finding cards. */
export default function FindingStream(
  {
    groups, groupBy, onGroupBy, search, onSearch, selected, scrollTo, onSelect, repo, linkedPr, summary, notices, empty, footer, tools,
  }: {
    groups: readonly FindingGroup[];
    groupBy: GroupBy;
    onGroupBy: (g: GroupBy) => void;
    search: string;
    onSearch: (q: string) => void;
    selected: number | null;
    scrollTo: number | null;
    onSelect: (id: number) => void;
    repo: string | undefined;
    linkedPr: number | null;
    summary: ReactNode;
    notices: ReactNode;
    empty: ReactNode;
    footer: ReactNode;
    tools: ReactNode;
  },
) {
  const list = useRef<HTMLDivElement>(null);
  return (
    <section className="bob-stream" aria-label="Findings">
      <div className="bob-stream-bar">
        <label className="bob-search">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
            <circle cx="11" cy="11" r="7" />
            <path d="m20 20-3.5-3.5" />
          </svg>
          <span className="bob-sr">Search findings</span>
          <input type="search" placeholder="Search file, message, id" value={search} onChange={(e) => onSearch(e.target.value)} />
        </label>
        <div className="bob-seg" role="group" aria-label="Group findings by">
          {GROUPINGS.map((g) => (
            <button
              key={g.id}
              type="button"
              className={groupBy === g.id ? "is-on" : ""}
              aria-pressed={groupBy === g.id}
              onClick={() => onGroupBy(g.id)}
            >
              {g.label}
            </button>
          ))}
        </div>
      </div>
      {(summary || tools) && (
        <div className="bob-stream-meta">
          <span>{summary}</span>
          {tools}
        </div>
      )}
      <div ref={list} className="bob-stream-list">
        {notices}
        {groups.length === 0
          ? empty
          : groups.map((group) => (
            <div key={group.key} className="bob-group" data-group={group.key}>
              <GroupHead group={group} repo={repo} linked={group.kind === "pr" && linkedPr !== null && group.pr === linkedPr} />
              <ul className="bob-cards">
                {group.findings.map((f) => (
                  <FindingCard
                    key={f.id}
                    finding={f}
                    selected={f.id === selected}
                    scrollTo={f.id === scrollTo}
                    onSelect={onSelect}
                    list={list}
                  />
                ))}
              </ul>
            </div>
          ))}
        {footer}
      </div>
    </section>
  );
}
